// Chrome-style horizontal tab strip that sits at the very top of the main work area.
// Tabs are rendered with a slight up-tilt and the active tab merges into the page
// background below it (mimicking browser-tab behaviour).
//
// The strip is horizontally scrollable when many tabs are open so it never overflows.
import { X } from "lucide-react";
import { useTabs } from "@/contexts/TabsContext";
import { MAX_TABS } from "@/lib/tabRegistry";

export default function TabBar() {
  const { openTabs, activePath, activateTab, closeTab } = useTabs();
  const visibleTabs = openTabs.filter((t) => !t.hidden);

  return (
    <div
      data-testid="tab-bar"
      className="bg-slate-100 border-b border-slate-300 flex items-end overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 sticky top-0 z-50"
      style={{ minHeight: "38px" }}
    >
      <div className="flex items-end gap-0.5 px-2 pt-1.5 min-w-max">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.path === activePath;
          return (
            <div
              key={tab.path}
              data-testid={`tab-${tab.path.replace(/\//g, "-")}`}
              role="tab"
              aria-selected={isActive}
              onClick={() => activateTab(tab.path)}
              onAuxClick={(e) => {
                // Middle-click closes the tab — exactly like a browser.
                if (e.button === 1) {
                  e.preventDefault();
                  closeTab(tab.path);
                }
              }}
              className={[
                "group flex items-center gap-2 pl-3 pr-2 py-1.5 text-xs cursor-pointer select-none transition-colors",
                "border border-b-0 rounded-t-md max-w-[200px] min-w-[120px]",
                isActive
                  ? "bg-white border-slate-300 text-slate-900 font-semibold relative z-10 -mb-px shadow-sm"
                  : "bg-slate-200 border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900",
              ].join(" ")}
              style={isActive ? { boxShadow: "0 -2px 0 0 #F97316 inset" } : undefined}
              title={tab.label}
            >
              {Icon && <Icon size={13} strokeWidth={1.75} className="shrink-0" />}
              <span className="truncate flex-1">{tab.label}</span>
              <button
                data-testid={`close-tab-${tab.path.replace(/\//g, "-")}`}
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.path);
                }}
                aria-label={`Close ${tab.label}`}
                className={[
                  "shrink-0 w-4 h-4 flex items-center justify-center rounded-sm",
                  isActive
                    ? "text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                    : "text-slate-400 hover:bg-slate-300 hover:text-slate-900 opacity-60 group-hover:opacity-100",
                ].join(" ")}
              >
                <X size={11} strokeWidth={2} />
              </button>
            </div>
          );
        })}
        <div className="text-[10px] text-slate-400 ml-2 pb-1 self-end whitespace-nowrap" data-testid="tab-counter">
          {visibleTabs.length} / {MAX_TABS} tabs
        </div>
      </div>
    </div>
  );
}
